import { formatSeconds } from '@capturi/ui-components'
import { Box, Flex, HStack, Icon, Tag, Text, VStack } from '@chakra-ui/react'
import { motion } from 'framer-motion'
import { type FC, useCallback, useState } from 'react'
import { MdKeyboardArrowDown, MdKeyboardArrowUp } from 'react-icons/md'

import { useCaptureEvent } from '../../../utils/analytics'
import { MagicBox, MagicBoxHeader } from '../../components/MagicBox'
import { SummaryBox } from '../../components/SummaryBox'

type Props = {
  user: {
    email: string
    name: string
  }
  team: string | null
  subject: string
  duration: number
  dateTime: string
  customSummary: string
  conversationUid: string
  keyTopic: string | null
  subKeyTopic: string | null
  rootCause: string | null
}

const formatDate = new Intl.DateTimeFormat('da-DK', {
  year: 'numeric',
  month: 'long',
  day: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
}).format
export const SingleConversation: FC<Props> = ({
  user,
  team,
  dateTime,
  duration,
  subject,
  customSummary,
  keyTopic,
  subKeyTopic,
  rootCause,
  conversationUid,
}) => {
  const formattedDuration = formatSeconds(duration)
  const [isCollapsed, setIsCollapsed] = useState(true)
  const [shouldRenderContent, setShouldRenderContent] = useState(false)
  const captureEvent = useCaptureEvent()

  const handleToggle = useCallback(() => {
    setIsCollapsed((s) => {
      const next = !s
      if (!next) {
        // Expanding - mount content immediately before animation
        setShouldRenderContent(true)
      }
      captureEvent(
        next ? 'PreviousInteractions_Expand' : 'PreviousInteractions_Collapse',
        { conversationUid },
      )
      return next
    })
  }, [captureEvent, conversationUid])

  const handleAnimationComplete = useCallback(() => {
    if (isCollapsed) {
      // Collapsed animation finished - unmount content
      setShouldRenderContent(false)
    }
  }, [isCollapsed])

  return (
    <motion.div
      style={{ overflow: 'hidden' }}
      initial={{ width: 'clamp(180px, 100%, 430px)' }}
      animate={{ width: isCollapsed ? 'clamp(180px, 100%, 430px)' : '100%' }}
      transition={{ type: 'spring', stiffness: 260, damping: 30 }}
    >
      <MagicBox
        data-group
        aria-expanded={!isCollapsed}
        cursor="pointer"
        onClick={handleToggle}
        _hover={{
          background: 'gray.50',
        }}
      >
        <MagicBoxHeader background="white">
          <VStack gap={1}>
            <Flex w="100%" justify="space-between" gap="4" alignItems="center">
              <Text
                fontWeight="semibold"
                overflow="hidden"
                textOverflow="ellipsis"
                noOfLines={1}
                wordBreak="break-all"
              >
                {subject}
              </Text>
              <HStack flexShrink={0} display={{ base: 'none', md: 'flex' }}>
                {team && <Tag>{team}</Tag>}
                <Text fontSize="sm" fontWeight="semibold">
                  {user.name}
                </Text>
              </HStack>
            </Flex>
            <Flex w="100%" justify="space-between" alignItems="top" gap={1}>
              <Text
                textColor="textMuted"
                fontSize="sm"
                flex="auto"
                overflow="hidden"
                textOverflow="ellipsis"
                wordBreak="break-all"
                noOfLines={1}
              >
                {keyTopic && subKeyTopic
                  ? `${keyTopic} > ${subKeyTopic}`
                  : keyTopic || subKeyTopic}
              </Text>
              <HStack
                alignItems="top"
                flexShrink={0}
                display={{ base: 'none', md: 'flex' }}
              >
                <Text color="greys.400" fontSize="xs">
                  {formattedDuration}&nbsp;|&nbsp;
                  {formatDate(new Date(dateTime))}
                </Text>
              </HStack>
            </Flex>
          </VStack>
        </MagicBoxHeader>
        <motion.div
          initial={false}
          animate={{ gridTemplateRows: isCollapsed ? '0fr' : '1fr' }}
          transition={{ duration: 0.25, ease: 'easeInOut' }}
          style={{ display: 'grid' }}
          aria-hidden={isCollapsed}
          onAnimationComplete={handleAnimationComplete}
        >
          {shouldRenderContent && (
            <Box overflow="hidden">
              <Box px={3} py={2}>
                <SummaryBox
                  conversationUid={conversationUid}
                  rootCause={rootCause}
                  customSummary={customSummary}
                />
              </Box>
            </Box>
          )}
        </motion.div>

        <Box
          background={
            isCollapsed
              ? 'none'
              : 'linear-gradient(180deg, rgba(255, 255, 255, 0.00) 17.66%, rgba(255, 255, 255, 0.92) 58.97%)'
          }
          border="none"
          borderBottomLeftRadius="2xl"
          borderBottomRightRadius="2xl"
          overflow="hidden"
          position="absolute"
          bottom="2px"
          left="2px"
          right="2px"
          display="flex"
          justifyContent="center"
          alignItems="center"
          _groupHover={{
            background:
              'linear-gradient(180deg, rgba(255, 255, 255, 0.00) 17.66%, rgba(255, 255, 255, 0.92) 58.97%)',
          }}
        >
          <Icon
            display={isCollapsed ? 'none' : 'block'}
            as={isCollapsed ? MdKeyboardArrowDown : MdKeyboardArrowUp}
            fontSize="20px"
            color="greys.600"
            _groupHover={{
              display: 'block',
            }}
          />
        </Box>
      </MagicBox>
    </motion.div>
  )
}
